/**
 * Storage failure tracking and fallback switching helper
 * Centralizes the logic for incrementing storage failure counts and
 * switching to memory-only mode when failures exceed the threshold.
 */

/**
 * Storage state interface for failure tracking
 */
export interface StorageState {
  useMemoryFallback: boolean;
  storageFailCount: number;
}

/**
 * Storage action dispatcher type
 * Matches the dispatch function from useReducer
 */
export type StorageActionDispatcher = (
  action:
    | { type: "INCREMENT_STORAGE_FAIL_COUNT" }
    | { type: "SET_STORAGE_FAIL_COUNT"; payload: number }
    | { type: "SET_USE_MEMORY_FALLBACK"; payload: boolean },
) => void;

/**
 * Logger interface matching the logger from loggingUtils
 */
export interface Logger {
  log: (message?: unknown, ...optionalParams: unknown[]) => void;
  warn: (message?: unknown, ...optionalParams: unknown[]) => void;
}

/**
 * Handles storage failure by incrementing the failure count and
 * switching to memory-only mode if the threshold is reached.
 *
 * @param storageState - Current storage state containing fail count and fallback status
 * @param dispatchStorage - Function to dispatch storage actions
 * @param logger - Logger instance for logging operations
 *
 * @remarks
 * **Concurrent Calls**: If this function is called multiple times rapidly (e.g., multiple
 * concurrent storage operations fail), each call may see the same `storageFailCount` value
 * before the reducer processes the increment. This can result in duplicate threshold warning
 * messages being logged. This behavior is acceptable given the rarity of rapid concurrent
 * failures, and correctness is maintained since the reducer handles the actual state transition
 * atomically. The duplicate warnings do not affect the correctness of the state management.
 */
export function handleStorageFailure(
  storageState: StorageState,
  dispatchStorage: StorageActionDispatcher,
  logger: Logger,
): void {
  // Check if this increment will cross the threshold for logging purposes
  const willCrossThreshold =
    storageState.storageFailCount + 1 >= 3 && !storageState.useMemoryFallback;

  // Dispatch increment action - reducer will atomically increment and handle threshold check
  dispatchStorage({ type: "INCREMENT_STORAGE_FAIL_COUNT" });

  logger.log(`AIChatWidget: Storage fail count incremented (was ${storageState.storageFailCount})`);

  // Log warning if threshold will be crossed (reducer handles the actual switch)
  if (willCrossThreshold) {
    logger.warn("AIChatWidget: Multiple storage failures detected, switching to memory-only mode");
  }
}
